import { useTranslation } from "react-i18next";
import { motion, Variants } from "framer-motion";
import { Element } from "react-scroll";
import { TbWorldPin } from "react-icons/tb";

import HeroImg from "src/assets/images/home/hero.webp";
import { scrollToSection } from "src/utils/scroll";

const fadeUp: Variants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i: number) => ({
        opacity: 1,
        y: 0,
        transition: {
            delay: i * 0.18,
            duration: 0.8,
            ease: "easeOut",
        },
    }),
};

const HeroSection: React.FC = () => {
    const { t } = useTranslation("home");

    return (
        <Element
            name="hero"
            className="relative isolate flex min-h-[520px] items-center justify-center overflow-hidden px-4 sm:min-h-[600px] lg:h-[calc(100vh-5rem)] lg:max-h-[760px]"
        >
            {/* Background image with slow zoom */}
            <div
                aria-hidden
                className="absolute inset-0 -z-20 animate-bg-scale bg-cover bg-center will-change-transform"
                style={{ backgroundImage: `url(${HeroImg})` }}
            />

            {/* Brand overlay */}
            <div
                aria-hidden
                className="absolute inset-0 -z-10 bg-gradient-to-b from-primary-900/75 via-primary-900/70 to-primary-800/95"
            />
            <div
                aria-hidden
                className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,rgba(66,154,237,.35),transparent_60%)]"
            />
            <div aria-hidden className="bg-dots absolute inset-0 -z-10 text-white/[0.06]" />

            {/* Content */}
            <div className="relative mx-auto flex max-w-4xl flex-col items-center py-24 text-center sm:py-28">
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    animate="visible"
                    custom={0}
                    className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-light tracking-brand text-primary-50 backdrop-blur-md"
                >
                    <TbWorldPin className="h-4 w-4 text-primary-200" />
                    {t("hero.company_name")}
                </motion.div>

                <motion.h1
                    variants={fadeUp}
                    initial="hidden"
                    animate="visible"
                    custom={1}
                    className="mb-5 max-w-3xl text-[1.75rem] font-bold capitalize leading-[1.3] tracking-brand text-white drop-shadow-sm rtl:leading-[1.6] sm:text-4xl lg:text-5xl lg:leading-[1.2] lg:rtl:leading-[1.55]"
                >
                    {t("hero.tagline")}
                </motion.h1>

                <motion.p
                    variants={fadeUp}
                    initial="hidden"
                    animate="visible"
                    custom={2}
                    className="mb-10 max-w-2xl text-[0.9375rem] font-light leading-[1.9] tracking-brand text-primary-50/90 sm:text-lg"
                >
                    {t("hero.description")}
                </motion.p>

                {/* Animated scroll button */}
                <motion.button
                    variants={fadeUp}
                    initial="hidden"
                    animate="visible"
                    custom={3}
                    type="button"
                    onClick={() => scrollToSection("aboutUs")}
                    aria-label={t("hero.explore")}
                    title={t("hero.explore")}
                    className="flex h-12 w-8 justify-center rounded-full border-2 border-white/50 pt-2.5 transition-colors duration-300 hover:border-white hover:bg-white/10"
                >
                    <span className="h-2.5 w-1 animate-scroll-dot rounded-full bg-white" />
                </motion.button>
            </div>

            {/* Bottom wave */}
            <svg
                aria-hidden
                className="absolute -bottom-px left-0 w-full text-white"
                viewBox="0 0 1440 60"
                preserveAspectRatio="none"
                style={{ height: "clamp(24px, 4vw, 60px)" }}
            >
                <path fill="currentColor" d="M0,32 C240,64 480,64 720,40 C960,16 1200,8 1440,32 L1440,60 L0,60 Z" />
            </svg>
        </Element>
    );
};

export default HeroSection;
